import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import type {
  ColDef,
  CellValueChangedEvent,
  ColumnResizedEvent,
  DragStoppedEvent,
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
} from 'ag-grid-community'
import { DATASHEET_URL_MAX, isSafeDatasheetUrl } from '@crm/shared'
import { patchItem } from '../../../shared/api/requestItems.api'
import { apiErrorText } from '../../../shared/api/errorText'
import type { SourceType } from '../../../shared/api/types'
import { toast } from '../../../shared/ui/toastStore'
import { useItemsGridSelection } from '../hooks/useItemsGridSelection'
import { useItemsUndoStack } from '../hooks/useItemsUndoStack'
import { CheckboxSetFilter } from '../../table/CheckboxSetFilter'
import { AG_GRID_LOCALE_RU } from '../../../shared/ag-grid/locale'
import { CustomsAnswerViewModal } from './CustomsAnswerViewModal'

type Props = {
  rowData: Record<string, unknown>[]
  columnDefs: ColDef[]
  isAdmin: boolean
  userRole: string | undefined
  requestId: string | undefined
  onSelectionChanged: (e: SelectionChangedEvent) => void
  onGridReady: (e: GridReadyEvent) => void
  onSaveColState: (api: GridApi) => void
  // Ввод цены в ячейку матрицы (Склад/Китай/РФ/Производители/Стоки) больше не
  // создаёт квоту молча — он открывает модалку создания квоты с предзаполненной
  // ценой. Само поле позиции при вводе не патчим (значение появится после
  // создания квоты в модалке).
  onCellPriceEntered?: (
    itemId: string,
    sourceType: SourceType,
    matrixLabel: string,
    price: string,
  ) => void
}

// Ценовые колонки матриц → SourceType квоты + русская метка для модалки.
const PRICE_COLS: Record<string, { source: SourceType; label: string }> = {
  price: { source: 'CHINA', label: 'Китай' }, // targetPrice = колонка «Китай»
  priceStock: { source: 'STOCK', label: 'Склад' },
  priceManufacturer: { source: 'MANUFACTURER', label: 'Производители' },
  priceRF: { source: 'RF', label: 'РФ' },
  priceOther: { source: 'OTHER', label: 'Стоки' },
}

export const ItemsGrid = forwardRef<AgGridReact, Props>(function ItemsGrid(
  {
    rowData,
    columnDefs,
    isAdmin,
    userRole,
    requestId,
    onSelectionChanged,
    onGridReady,
    onSaveColState,
    onCellPriceEntered,
  },
  gridRef,
) {
  const queryClient = useQueryClient()

  // Внутренний ref нужен чтобы хук диапазонного выделения работал с AG Grid API.
  // Внешний ref (через forwardRef) связываем через useImperativeHandle.
  const innerRef = useRef<AgGridReact>(null)
  useImperativeHandle(gridRef, () => innerRef.current as AgGridReact, [])

  const { onCellMouseDown, onCellMouseOver, getCellStyle, suppressKeyboardEvent } =
    useItemsGridSelection(innerRef)
  const { push: pushUndo } = useItemsUndoStack(requestId)

  // Для отдела продаж (MANAGER/ROP/ADMIN) позиции всегда в порядке заявки
  // (priority asc, createdAt asc из API). Клики по заголовкам не должны
  // переставлять строки — иначе после ввода цены позиция «падает вниз» при
  // активной сортировке по priceStock/priceRF. У закупщиков (PROCUREMENT/ROS)
  // сортировка разрешена — им важно видеть позиции по цене/статусу.
  const isSales =
    isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ASSISTANT'
  const canSelectRows = isSales || userRole === 'PROCUREMENT' || userRole === 'ROS'

  // Виртуализация AG Grid: шапка фиксируется автоматически, в DOM только видимые строки.
  // domLayout="autoHeight" убран намеренно — при 500+ позициях он клал браузер.
  // Высота адаптируется к вьюпорту с разумными min/max, чтобы всегда оставалась полоса прокрутки.
  return (
    <>
      <CustomsAnswerViewModal />
      <div className="crm-table ag-theme-alpine overflow-x-auto h-[calc(100vh-340px)] min-h-[400px] max-h-[800px]">
        <AgGridReact
          ref={innerRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: !isSales,
            resizable: true,
            suppressMovable: false,
            // Минимум 20px: колонку можно сжать почти в ноль, но resize-handle
            // всё ещё захватывается.
            minWidth: 20,
            // Подсветка выделенного диапазона — см. useItemsGridSelection.
            cellStyle: getCellStyle,
            // Стрелки вверх/вниз в режиме редактирования: коммит + переход
            // на соседнюю ячейку. Позволяет быстро вводить цены подряд.
            suppressKeyboardEvent,
            // Воронка-фильтр в каждом заголовке (поиск + чекбоксы по уникальным
            // значениям колонки). Тот же UX, что в RequestsPage / TablePage.
            filter: CheckboxSetFilter,
            floatingFilter: false,
          }}
          localeText={AG_GRID_LOCALE_RU}
          // Базовая высота 56px (была 48): wrapText+autoHeight на колонках
          // «Квота/💬», «Заметка», «Комментарий», «proc_*» дают переносы; при
          // 48px бейджи закупщиков накладывались на следующую строку (аудит 23.04).
          rowHeight={56}
          headerHeight={36}
          maintainColumnOrder
          suppressDragLeaveHidesColumns
          // Excel-подобное выделение: одиночный клик = ячейка + якорь,
          // Shift+клик расширяет прямоугольный диапазон, drag тоже расширяет.
          // Ctrl+C / Cmd+C копирует в TSV (вставляется в Excel/Sheets).
          // Редактирование — двойной клик / Enter / F2.
          onCellMouseDown={onCellMouseDown}
          onCellMouseOver={onCellMouseOver}
          ensureDomOrder
          getRowId={(p) => String(p.data.id)}
          rowSelection={
            canSelectRows
              ? {
                  mode: 'multiRow',
                  enableClickSelection: false,
                  checkboxes: false,
                  headerCheckbox: false,
                }
              : undefined
          }
          onSelectionChanged={onSelectionChanged}
          onGridReady={onGridReady}
          onCellValueChanged={async (e: CellValueChangedEvent) => {
            const itemId = e.data.id as string
            const colId = e.colDef.colId
            // Undo-стэк: запоминаем старое значение до PATCH-а, чтобы по Ctrl+Z
            // можно было откатить случайную правку (см. useItemsUndoStack).
            if (colId && e.oldValue !== e.newValue) {
              pushUndo({ itemId, colId, oldValue: e.oldValue })
            }
            // Поддерживаем и number, и строковые Decimal из ответа Prisma
            const toNum = (v: unknown): number | null => {
              if (v == null || v === '') return null
              const n = typeof v === 'number' ? v : parseFloat(String(v))
              return isFinite(n) && n > 0 ? n : null
            }
            // Цена-колонки матрицы: ввод цены НЕ патчит поле и не создаёт квоту
            // молча — он открывает модалку создания квоты с предзаполненной ценой.
            // Откатываем визуальный ввод (реальное значение поля появится после
            // создания квоты), затем передаём введённую цену наверх.
            const priceCol = PRICE_COLS[colId ?? '']
            if (priceCol) {
              const fieldName = e.colDef.field
              const entered = fieldName
                ? toNum((e.data as Record<string, unknown>)[fieldName])
                : null
              if (fieldName) {
                ;(e.data as Record<string, unknown>)[fieldName] = e.oldValue
                e.api.refreshCells({ rowNodes: [e.node], force: true })
              }
              if (entered != null && entered > 0) {
                onCellPriceEntered?.(itemId, priceCol.source, priceCol.label, String(entered))
              }
              return
            }
            try {
              if (colId === 'comment')
                await patchItem(itemId, {
                  comment: (e.data.comment as string) || null,
                })
              else if (colId === 'kpNote')
                await patchItem(itemId, {
                  kpNote: (e.data.kpNote as string) || null,
                })
              else if (colId === 'partNumber') {
                const pn = String(e.data.partNumber ?? '').trim()
                if (!pn) throw new Error('Партномер не может быть пустым')
                await patchItem(itemId, { partNumber: pn })
              } else if (colId === 'brand') {
                const v = (e.data.brand as string | null) ?? null
                await patchItem(itemId, { brand: v })
              } else if (colId === 'category') {
                const v = ((e.data.category as string | null) ?? '').trim()
                await patchItem(itemId, { category: v || null })
              } else if (colId === 'datasheetUrl') {
                // Пусто — снять ссылку; иначе только http(s) (как проверяет сервер).
                const url = ((e.data.datasheetUrl as string | null) ?? '').trim()
                if (url.length > DATASHEET_URL_MAX)
                  throw new Error(`Ссылка на даташит длиннее ${DATASHEET_URL_MAX} символов`)
                if (url && !isSafeDatasheetUrl(url))
                  throw new Error('Ссылка на даташит должна начинаться с http:// или https://')
                await patchItem(itemId, { datasheetUrl: url || null })
              } else if (colId === 'noteForProcurement') {
                const v = (e.data.noteForProcurement as string | null) ?? null
                await patchItem(itemId, { noteForProcurement: v })
              } else if (colId === 'qty')
                await patchItem(itemId, {
                  quantity: e.data.quantity as number,
                })
              else return
              // НЕ инвалидируем кэш заявки на каждый ввод ячейки:
              // AG Grid уже содержит актуальное значение (valueSetter обновил e.data),
              // а refetch всей заявки при активном вводе приводил к гонке —
              // пока одна ячейка сохраняется, refetch перезаписывал свежие
              // значения в соседних ячейках. Помечаем как stale без refetch.
              queryClient.invalidateQueries({
                queryKey: ['requests', requestId],
                refetchType: 'none',
              })
            } catch (err) {
              // Откатываем значение ячейки при ошибке API и говорим почему. Раньше
              // откат был молчаливым: правка просто исчезала, и казалось, что
              // таблица «не сохраняет» (№310).
              if (e.colDef.field) {
                ;(e.data as Record<string, unknown>)[e.colDef.field] = e.oldValue
                e.api.refreshCells({ rowNodes: [e.node], force: true })
              }
              toast.error(apiErrorText(err, 'Не удалось сохранить изменение — значение возвращено'))
            }
          }}
          onDragStopped={(e: DragStoppedEvent) => onSaveColState(e.api)}
          onColumnResized={(e: ColumnResizedEvent) => {
            if (e.finished) onSaveColState(e.api)
          }}
        />
      </div>
    </>
  )
})
